import React, { useState } from 'react';
import { X, Copy, Check, Sparkles, ArrowUpRight } from 'lucide-react';
import { businessInfo } from '../data/mock';

/**
 * SummerWelcomeModal — first-visit popup combining:
 *   1) Peptide Wellness Programs announcement (primary CTA → Book Consultation)
 *   2) Optional path: drop email for a 15% off code on à la carte treatments
 *
 * Replaces the previous standalone CouponModal. Both lead paths preserved.
 */
const SummerWelcomeModal = ({ isOpen, onClose, onSubmit, isLoading, couponData }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [showCouponForm, setShowCouponForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    await onSubmit({ email, name }, { source: 'summer_welcome_modal' });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(couponData?.couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePeptideCta = () => {
    // Close + soft scroll-to-peptides
    onClose();
    setTimeout(() => {
      const target = document.getElementById('peptides');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="summer-welcome-modal"
    >
      <div
        className="absolute inset-0 bg-onyx/85 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative bg-carbon border border-pewter/70 max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-silverx hover:text-blush transition-colors z-10"
          aria-label="Close"
          data-testid="summer-welcome-close"
        >
          <X size={18} />
        </button>

        {couponData ? (
          /* Coupon claimed success state */
          <div className="p-8 md:p-10">
            <div className="text-center mb-6">
              <p className="eyebrow mb-4">Welcome to Privé</p>
              <h3 className="font-serif text-ivory text-3xl">You're on the list.</h3>
            </div>

            <div className="border border-dashed border-blush/70 p-6 mb-6 text-center">
              <p className="eyebrow mb-3 text-blush">Your Code</p>
              <div className="flex items-center justify-center gap-3">
                <span
                  className="font-serif text-ivory text-2xl md:text-3xl tracking-wider"
                  data-testid="summer-welcome-coupon-code"
                >
                  {couponData.couponCode}
                </span>
                <button
                  onClick={handleCopy}
                  className="p-2 text-silverx hover:text-blush transition-colors"
                  aria-label="Copy code"
                  data-testid="summer-welcome-coupon-copy"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <p className="font-serif brand-italic chrome-rose text-lg mt-4">
                15% off your first treatment
              </p>
            </div>

            <a
              href={businessInfo.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="summer-welcome-book-btn"
              className="btn-pill w-full inline-flex justify-center items-center gap-3 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
            >
              Book Consultation
              <ArrowUpRight size={14} />
            </a>
            <p className="text-silverx/70 text-[11px] text-center tracking-wider uppercase mt-4">
              Valid for 30 days.
            </p>
          </div>
        ) : (
          <>
            {/* Peptide Hero */}
            <div className="p-8 md:p-10 border-b border-pewter/60 relative">
              {/* Soft glow */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    'radial-gradient(circle at 50% 0%, rgba(236,138,174,0.12) 0%, transparent 65%)',
                }}
              />

              <div className="relative">
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles size={14} className="text-blush" />
                  <span className="eyebrow chrome-rose">Get Snatched This Summer</span>
                </div>
                <h2 className="font-serif text-ivory text-3xl md:text-4xl leading-[1.1] mb-4 text-balance">
                  Premium Peptide
                  <br />
                  <span className="brand-italic chrome-rose">Wellness Programs.</span>
                </h2>
                <p className="text-platinum text-sm md:text-[15px] mt-4 leading-relaxed">
                  Stay consistent, accountable, and focused on your transformation. Professional
                  administration, luxury experience, results-driven cadence — built for busy
                  lifestyles.
                </p>

                <ul className="space-y-2 mt-6 mb-7">
                  {[
                    'Professional administration',
                    'Luxury wellness experience',
                    'Accountability & consistency',
                    'Body transformation support',
                  ].map((point) => (
                    <li
                      key={point}
                      className="flex items-center gap-3 text-platinum text-sm leading-relaxed"
                    >
                      <Check className="w-3.5 h-3.5 text-blush flex-shrink-0" strokeWidth={2} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-[10px] tracking-[0.28em] uppercase text-silverx mb-5">
                  Limited founding-client spots available
                </p>

                <button
                  type="button"
                  onClick={handlePeptideCta}
                  data-testid="summer-welcome-peptide-cta"
                  className="btn-pill w-full inline-flex justify-center items-center gap-3 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
                >
                  View Peptide Programs
                  <ArrowUpRight size={14} />
                </button>
              </div>
            </div>

            {/* Coupon Path (collapsed by default) */}
            <div className="p-7 md:p-8 bg-onyx/40">
              {!showCouponForm ? (
                <button
                  type="button"
                  onClick={() => setShowCouponForm(true)}
                  data-testid="summer-welcome-coupon-toggle"
                  className="w-full text-left flex items-center justify-between gap-3 text-platinum hover:text-blush transition-colors"
                >
                  <span className="text-[11px] tracking-[0.22em] uppercase">
                    Not ready for a program? <span className="chrome-rose">Get 15% off any treatment →</span>
                  </span>
                </button>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <p className="text-[11px] tracking-[0.22em] uppercase text-silverx mb-3">
                    Your private 15% code — drop your email
                  </p>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name (optional)"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 text-sm outline-none transition-colors"
                    data-testid="summer-welcome-name"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    required
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 text-sm outline-none transition-colors"
                    data-testid="summer-welcome-email"
                  />
                  {error && <p className="text-red-300 text-xs">{error}</p>}
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="summer-welcome-coupon-submit"
                    className="btn-pill w-full inline-flex justify-center items-center gap-3 px-6 py-3 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.24em] uppercase hover:border-blush hover:text-blush disabled:opacity-50"
                  >
                    {isLoading ? 'Sending…' : 'Claim My 15% Code'}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SummerWelcomeModal;
